/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * `/` — redirect theo license ∩ availablePhanHe (ưu tiên BSC → VH → QT), không có thì /intro.
 * setPhanHe trước khi replace dashboard.
 */
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { Spin } from 'antd';
import { ROUTES } from '@/utils/pathRoutes';
import { useAuthStore } from '@/store/useAuthStore';
import { CONSTANTS } from '@/utils/constants';
import { hasAvailablePhanHe } from '@/utils/phanHeRoles';
import {
  getLicenseInitParam,
  readLicenseFromStorage,
  saveLicenseToStorage,
} from '@/utils/licenseStorage';
import { apiUrlApi } from '@/services/api-sync/sync-api';
import type { License } from '@/types/license';

const withTrailingSlash = (route: string) => (route.endsWith('/') ? route : `${route}/`);

const hasPhanHe = (raw: string | null | undefined, key: string) =>
  (raw ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .some((item) => item === key);

/** `/` → phân hệ theo rule intro; ưu tiên BSC | Văn hóa | Quy trình */
export default function HomePage() {
  const router = useRouter();
  const tenantID = useAuthStore((state) => state.tenantID);
  const profile = useAuthStore((state) => state.profile);
  const setPhanHe = useAuthStore((state) => state.setPhanHe);
  const redirectingRef = useRef(false);

  useEffect(() => {
    if (!router.isReady || redirectingRef.current) return;

    let cancelled = false;

    const resolveAndRedirect = async () => {
      redirectingRef.current = true;

      let licensePhanHe = '';
      if (tenantID) {
        const cached = readLicenseFromStorage(tenantID);
        if (cached !== undefined && cached !== null) {
          licensePhanHe = cached.phanHe ?? '';
        } else {
          try {
            const res: any = await apiUrlApi.getLicense(getLicenseInitParam(tenantID));
            if (res?.success) {
              const data = (res.data ?? null) as License | null;
              saveLicenseToStorage(tenantID, data);
              licensePhanHe = data?.phanHe ?? '';
            }
          } catch (error) {
            console.error('[home] load license failed', error);
          }
        }
      }

      if (cancelled) return;

      // Giống intro: license.phanHe ∩ profile.availablePhanHe
      const showBSC =
        hasPhanHe(licensePhanHe, CONSTANTS.PHAN_HE.BSC) &&
        hasAvailablePhanHe(profile, CONSTANTS.PHAN_HE.BSC);
      const showVanHoa =
        hasPhanHe(licensePhanHe, CONSTANTS.PHAN_HE.VAN_HOA) &&
        hasAvailablePhanHe(profile, CONSTANTS.PHAN_HE.VAN_HOA);
      const showQuyTrinh =
        hasPhanHe(licensePhanHe, CONSTANTS.PHAN_HE.QUY_TRINH) &&
        hasAvailablePhanHe(profile, CONSTANTS.PHAN_HE.QUY_TRINH);

      // Ưu tiên: BSC → Văn hóa → Quy trình
      if (showBSC) {
        setPhanHe(CONSTANTS.PHAN_HE.BSC);
        await router.replace(withTrailingSlash(ROUTES.DASHBOARD));
        return;
      }
      if (showVanHoa) {
        setPhanHe(CONSTANTS.PHAN_HE.VAN_HOA);
        await router.replace(withTrailingSlash(ROUTES.VAN_HOA.DASHBOARD));
        return;
      }
      if (showQuyTrinh) {
        setPhanHe(CONSTANTS.PHAN_HE.QUY_TRINH);
        await router.replace(withTrailingSlash(ROUTES.QUY_TRINH.DASHBOARD));
        return;
      }

      await router.replace(ROUTES.INTRO);
    };

    void resolveAndRedirect().catch((error) => {
      console.error('[home] redirect failed', error);
      redirectingRef.current = false;
      void router.replace(ROUTES.INTRO);
    });

    return () => {
      cancelled = true;
    };
  }, [router, router.isReady, tenantID, profile, setPhanHe]);

  return (
    <div className="flex min-h-[80vh] w-full items-center justify-center bg-white">
      <Spin size="large" />
    </div>
  );
}
